import { useCallback, useEffect, useState } from 'react'
import { isEqual } from 'lodash-es'
import {
  AutopilotTaskDto,
  SearchTaskRequest,
  SearchTasksResponse,
  useSearchAllTasks,
} from '@urbanx/agx.activities.client'
import { TaskFilterState } from './TaskFilters/TaskFiltersState'

const PAGE_SIZE = 10

const createApiSafeArray = <T,>(arr?: T[]) => {
  return arr === undefined || arr.length === 0 ? undefined : arr
}

const useTaskSearch = (
  taskFilters?: TaskFilterState,
  filtersPending?: boolean
) => {
  const [currentFilters, setCurrentFilters] = useState<
    TaskFilterState | undefined
  >(taskFilters)
  const [tasksResponse, setTasksResponse] = useState<
    SearchTasksResponse | undefined
  >(undefined)
  const [tasks, setTasks] = useState<AutopilotTaskDto[]>([])
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined)

  const { searchAllTasks, searchAllTasksPending } = useSearchAllTasks({
    onSuccess: (data, request) => {
      setTasksResponse(data)
      setTasks((prev) => {
        const allTasks = [...prev, ...(data?.tasks ?? [])]
        const uniqueTasks = allTasks.filter((task, idx, arr) => {
          return (
            arr.findIndex(
              (t) =>
                t.campaignId === task.campaignId &&
                t.contractId === task.contractId &&
                t.category?.taskType === task.category?.taskType
            ) === idx
          )
        })
        return uniqueTasks
      })
      if (request?.continuationToken === undefined) {
        setTotalCount(data?.totalCount)
      }
    },
    onError: (error) => {
      console.log(error)
    },
  })

  const search = useCallback(
    (fresh: boolean) => {
      if (!taskFilters) return

      const searchObj: SearchTaskRequest = {
        pageSize: PAGE_SIZE,
        continuationToken: fresh ? undefined : tasksResponse?.continuationToken,
        requireQueryCount: fresh,
        states: createApiSafeArray(taskFilters.states),
        teamTypes: createApiSafeArray(taskFilters.teamTypes),
        stages: createApiSafeArray(taskFilters.stages),
        agencyIds: createApiSafeArray(taskFilters.agencyIds),
        assignedToUserIds: createApiSafeArray(taskFilters.assignedToUserIds),
        taskDue: taskFilters.taskDue,
        addressSearchText: taskFilters.addressSearchText,
      }

      searchAllTasks(searchObj)
    },
    [searchAllTasks, tasksResponse?.continuationToken, taskFilters]
  )

  const refreshSearch = useCallback(() => {
    setTasksResponse(undefined)
    setTasks([])

    search(true)
  }, [search])

  useEffect(() => {
    search(true)
  }, [])

  const loadMore = useCallback(() => {
    if (searchAllTasksPending) return

    if (!tasksResponse?.continuationToken) return

    search(false)
  }, [
    search,
    currentFilters,
    tasksResponse?.continuationToken,
    searchAllTasksPending,
  ])

  useEffect(() => {
    if (filtersPending) return

    if (isEqual(currentFilters, taskFilters)) return

    setCurrentFilters(taskFilters)
    setTasksResponse(undefined)
    setTasks([])

    search(true)
  }, [taskFilters, filtersPending])

  const handleTaskUpdate = useCallback((taskUpdate: any) => {
    setTasks((prevTasks) => {
      const taskIndex = prevTasks.findIndex(
        (task) =>
          task.campaignId === taskUpdate.campaignId &&
          task.contractId === taskUpdate.contractId &&
          task.category?.taskType === taskUpdate.taskType
      )

      if (taskIndex === -1) {
        return prevTasks
      }

      // If task is completed, remove it from the list
      if (taskUpdate.isCompleted) {
        return prevTasks.filter((_, idx) => idx !== taskIndex)
      }

      const updatedTask: AutopilotTaskDto = {
        ...prevTasks[taskIndex],
        assignedTo: taskUpdate.assignee,
        dueDate: (taskUpdate.dueDate ?? prevTasks[taskIndex].dueDate) as
          | Date
          | undefined,
        inProgress: taskUpdate.inProgress ?? prevTasks[taskIndex].inProgress,
        isCompleted: taskUpdate.isCompleted ?? prevTasks[taskIndex].isCompleted,
        isLocked: taskUpdate.isLocked ?? prevTasks[taskIndex].isLocked,
      }

      return [
        ...prevTasks.slice(0, taskIndex),
        updatedTask,
        ...prevTasks.slice(taskIndex + 1),
      ]
    })
  }, [])

  return {
    hasMore: !!tasksResponse?.continuationToken,
    totalCount: totalCount,
    tasks,
    searchAllTasksPending,
    loadMore,
    handleTaskUpdate,
    refreshSearch,
  }
}

export default useTaskSearch
